import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { getEventDay, type EventDayStats } from '../../lib/venue';
import { ApiError } from '../../lib/apiClient';
import { formatDate } from '../../lib/format';
import './VenuePages.css';

/**
 * Event-day dashboard for the venue.
 *
 * AGGREGATES ONLY, and that is the whole point of the screen. The engine's query
 * names no buyer column at all — a venue learns how many people came through the
 * door and roughly when, never who they were. That matches the rule organizer
 * analytics and sponsor analytics already follow.
 *
 * The arrival curve is a bucketed time dimension (scans per hour), not a list of
 * arrivals. It is the closest this surface gets to individual data, and it stops
 * well short.
 *
 * The engine gates this on the booking being CONFIRMED (409 otherwise), so a
 * venue cannot read attendance for a show it only received a request for.
 */

function Metric({
  value, label, sub, gold,
}: { value: string | number; label: string; sub?: string; gold?: boolean }) {
  return (
    <div className={`ven-metric${gold ? ' ven-metric--gold' : ''}`}>
      <span className="ven-metric__value">{value}</span>
      <span className="ven-metric__label">{label}</span>
      {sub && <span className="ven-metric__sub">{sub}</span>}
    </div>
  );
}

export function VenueEventDay() {
  const { eventId = '' } = useParams();
  const [stats, setStats] = useState<EventDayStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getEventDay(eventId)
      .then((r) => { if (!cancelled) setStats(r); })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 409) {
          setError('Attendance is only available once you have confirmed the booking.');
        } else if (err instanceof ApiError && err.status === 403) {
          setError('That event is not booked at your venue.');
        } else {
          setError(err instanceof ApiError ? err.message : 'Could not load event-day stats.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [eventId]);

  if (loading) return <p className="ven-status">Loading…</p>;
  if (error) {
    return (
      <div className="ven-page ven-page--narrow">
        <nav className="ven-breadcrumb"><Link to="/venue/bookings">← Bookings</Link></nav>
        <p className="ven-status ven-error" role="alert">{error}</p>
      </div>
    );
  }
  if (!stats) return null;

  const peak = Math.max(1, ...stats.arrivalsByHour.map((h) => h.scans));
  const capacityUse = stats.expectedAttendance
    ? Math.round((stats.checkedIn / stats.expectedAttendance) * 100)
    : null;
  // Attempts beyond valid scans are duplicates, wrong-event or already-used tickets.
  const rejected = stats.totalScanAttempts - stats.validScans;

  return (
    <div className="ven-page">
      <nav className="ven-breadcrumb"><Link to="/venue/bookings">← Bookings</Link></nav>

      <header className="ven-header">
        <div>
          <h1 className="ven-title">{stats.title}</h1>
          <p className="ven-lede">
            {stats.eventDate ? formatDate(stats.eventDate) : 'Date TBC'} · door counts for
            your room
          </p>
        </div>
      </header>

      <div className="ven-metrics">
        <Metric value={stats.checkedIn} label="Checked in" gold
          sub={`${Math.round(stats.checkInRate * 100)}% of valid tickets`} />
        <Metric value={stats.validTickets} label="Valid tickets" />
        <Metric
          value={stats.expectedAttendance ?? ' | '}
          label="Expected"
          sub={capacityUse != null ? `${capacityUse}% of expectation` : undefined}
        />
        <Metric value={stats.totalScanAttempts} label="Scan attempts"
          sub={rejected > 0 ? `${rejected} not admitted` : 'all admitted'} />
      </div>

      <section className="ven-section">
        <div className="ven-section__head">
          <h2 className="ven-section__title">Arrivals by hour</h2>
        </div>
        <p className="ven-section__note">
          Valid scans grouped into hourly buckets, useful for staffing the door. Voleto
          does not share who attended with the venue, only how many and when.
        </p>
        {stats.arrivalsByHour.length === 0 ? (
          <p className="ven-hint">No one has been scanned in yet.</p>
        ) : (
          <div className="ven-card">
            <div className="ven-curve">
              {stats.arrivalsByHour.map((h) => (
                <div key={h.hour} className="ven-curve__row">
                  <span className="ven-curve__hour">
                    {new Intl.DateTimeFormat('en-US', { hour: 'numeric' }).format(new Date(h.hour))}
                  </span>
                  <div className="ven-curve__track">
                    <div className="ven-curve__fill" style={{ width: `${(h.scans / peak) * 100}%` }} />
                  </div>
                  <span className="ven-curve__count">{h.scans}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
